import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PartyPopper } from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { celebrationSummary, formatAmount } from '../../utils/celebrationCopy';
import Celebration from './Celebration';

/**
 * "We just invoiced Acme Corp $12,450. Way to go, team!"
 *
 * Presentation only — TeamCelebration owns the fetch and the acknowledgement.
 *
 * Several invoices collapse into one modal with a list rather than one
 * celebration each: coming back from a long weekend should feel like good news,
 * not a queue to click through.
 */

export default function TeamCelebrationModal({ celebrations = [], moreCount = 0, cheer = 0, onDismiss }) {
  const isOpen = celebrations.length > 0;

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onDismiss?.(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onDismiss]);

  if (!isOpen) return null;

  const isSingle = celebrations.length === 1;

  return (
    <>
      <Celebration trigger={cheer} />
      {createPortal(
        <div
          // bg-black/50, not `bg-black bg-opacity-50` — Tailwind v4 dropped the
          // bg-opacity-* utilities, so that pairing renders a fully opaque
          // backdrop.
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="team-celebration-title"
        >
          <div className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full sm:max-w-md max-h-[85vh] flex flex-col">
            <div className="p-4 sm:p-6 pb-3 text-center">
              <PartyPopper className="w-8 h-8 text-activ-teal mx-auto mb-2" aria-hidden="true" />
              <h2 id="team-celebration-title" className="text-lg font-bold text-activ-navy">
                Way to go, team!
              </h2>
              <p className="text-sm text-gray-600 mt-1">{celebrationSummary(celebrations)}</p>
            </div>

            {!isSingle && (
              <div className="px-4 sm:px-6 overflow-y-auto">
                <ul className="divide-y divide-ae-border border border-ae-border rounded-lg">
                  {celebrations.map(item => (
                    <li key={item.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="text-sm text-activ-navy min-w-0 truncate">{item.customerName}</span>
                      <span className="text-sm font-medium text-activ-navy shrink-0 tabular-nums">
                        {formatAmount(item.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
                {moreCount > 0 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">+{moreCount} more</p>
                )}
              </div>
            )}

            <div className="p-4 sm:p-6 pt-4">
              <button
                type="button"
                onClick={onDismiss}
                autoFocus
                className="w-full px-4 py-2.5 bg-activ-teal text-white rounded-lg text-sm font-medium hover:bg-ae-aqua-700 transition-colors min-h-[44px]"
              >
                Nice
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
